import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Package,
  Plus,
  Trash2,
  Search,
  AlertCircle,
  Calendar,
  Hash,
  Loader2,
} from "lucide-react";

const Damages = () => {
  const searchRef = useRef();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [drugsDropdown, setDrugsDropdown] = useState(true);
  const [drugs, setDrugs] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const listContainerRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [insertedDrugs, setInsertedDrugs] = useState([]);
  const [drugBatches, setDrugBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [saleData, setSaleData] = useState({
    branch: activeSession?.branch?.id,
  });
  const [recordingSale, setRecordingSale] = useState(false);
  const [totalCost, setTotalCost] = useState(0.0);
  const [quantityRequested, setQuantityRequested] = useState("");

  const updateSaleData = (e) => {
    setSaleData({
      ...saleData,
      [e.target.name]: e.target.value,
    });
  };

  const selectDrug = (drug) => {
    if (parseFloat(drug?.quantity) <= 0) {
      Swal.fire("", "This drug is out of stock", "error");
      return false;
    } else {
      getBatches(drug?.drug?.id);
      setSelectedDrug(drug);
    }
    setDrugsDropdown(false);
    setSearch("");
    setDrugs([]);
  };



  const getDrugs = async (search_query) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/${activeSession?.branch?.id}?paginated=false&search=${search_query}`,
        "GET"
      );
      setDrugs(data);
    } catch (error) {
      console.error("Error fetching users data:", error);
    } finally {
      setLoading(false);
      setSelectedResultIndex(-1);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    if (debouncedSearch.length >= 3) {
      getDrugs(debouncedSearch);
    } else {
      setDrugs([]);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (drugsDropdown) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 0);
    } else {
      setSelectedResultIndex(-1);
    }
  }, [drugsDropdown]);

  const handleSearchKeyDown = (e) => {
    if (!drugs.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedResultIndex((prev) =>
        prev < drugs.length - 1 ? prev + 1 : prev
      );
      // specific scrolling logic if needed
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedResultIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedResultIndex >= 0 && selectedResultIndex < drugs.length) {
        selectDrug(drugs[selectedResultIndex]);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDrugsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedResultIndex >= 0 && listContainerRef.current) {
      const selectedElement = listContainerRef.current.querySelector(
        `#drug-result-${selectedResultIndex}`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedResultIndex]);

  const getBatches = async (drug) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/batches/list?paginated=false&branch=${activeSession?.branch?.id}&drug=${drug}`,
        "GET"
      );
      setDrugBatches(data);
    } catch (error) {
      console.error("Error fetching users data:", error);
    } finally {
      setLoading(false);
    }
  };

  const recordSales = async (e) => {
    e.preventDefault();
    if (insertedDrugs.length <= 0) {
      return toast.error("Insert at least one drug");
    }
    Swal.fire({
      icon: "question",
      title: "Are you sure you want to record the damage?",
      showDenyButton: true,
      confirmButtonText: "Yes, Proceed!",
      denyButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setRecordingSale(true);
          const body = JSON.stringify({
            ...saleData,
            branch: activeSession?.branch?.id,
            session: activeSession?.session,
            sale_items: insertedDrugs,
          });

          const data = await requestWithAuth(
            `/inventory/damage/`,
            "POST",
            body,
            {
              "Content-Type": "application/json",
            }
          );

          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Damage Recorded Successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          if (error.response?.data?.error) {
            for (const property in error.response.data.error) {
              toast.error(
                `${property} error: ${error.response.data.error[property]}`
              );
            }
          }
          if (error.response?.status === 500) {
            toast.error("Something went wrong!");
          }
        } finally {
          setRecordingSale(false);
        }
      }
    });
  };

  const insertDrug = () => {
    if (selectedBatch) {
      if (parseInt(quantityRequested) > parseInt(selectedBatch?.quantity)) {
        return toast.error(
          "Quantity requested is greater than the batch quantity"
        );
      }
    }

    if (selectedDrug) {
      if (quantityRequested) {
        const total_cost =
          parseFloat(selectedDrug?.drug?.cost_price) *
          parseFloat(quantityRequested);
        const newInsertedDrugs = [
          ...insertedDrugs,
          {
            drug: selectedDrug?.drug?.id,
            drug_name: selectedDrug?.drug?.name,
            quantity: quantityRequested,
            unit_price: selectedDrug?.drug?.cost_price,
            batch: selectedBatch?.id,
            total_cost: total_cost.toFixed(2),
          },
        ];
        setSelectedDrug({});
        setSelectedBatch({});
        setInsertedDrugs(newInsertedDrugs);
        setQuantityRequested("");

        let total = 0;
        newInsertedDrugs.map((item) => {
          total += parseFloat(item?.total_cost);
        });
        setTotalCost(total.toFixed(2));
      } else {
        toast.error("Please enter the quantity");
      }
    } else {
      toast.error("Select a drug first");
    }
  };

  const removeInsertedDrug = (index) => {
    const newInsertedDrugs = [...insertedDrugs];
    newInsertedDrugs.splice(index, 1);
    setInsertedDrugs(newInsertedDrugs);
    let total = 0;
    newInsertedDrugs.map((item) => {
      total += parseFloat(item?.total_cost);
    });
    setTotalCost(total.toFixed(2));
  };



  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
            <span>Inventory</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">
              Damages & Internal Usage
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Record Damages & Internal Usage
          </h1>
          <p className="text-gray-600 mt-1">
            Track damaged, expired, or internally used inventory items
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <form
            onSubmit={(e) => recordSales(e)}
            className="lg:col-span-2 space-y-6"
          >
            {/* Branch & Type Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-blue-500" />
                Damage Details
              </h3>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source Branch
                  </label>
                  <input
                    disabled
                    type="text"
                    value={activeSession?.branch?.name}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Damage Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="usage_type"
                    required
                    onChange={(e) => updateSaleData(e)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select type...</option>
                    <option value="Breakages/Damages">Breakages/Damages</option>
                    <option value="Expired">Expired</option>
                    <option value="Internal Usage">Internal Usage</option>
                    <option value="Protocol">Protocol</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Drug Selection Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-500" />
                Select Drug
              </h3>

              <div className="space-y-4">
                {/* Drug Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Drug Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative" ref={dropdownRef}>
                    <div
                      className="relative cursor-pointer border border-gray-300 rounded-lg px-4 py-2.5 text-sm bg-white hover:border-blue-500 transition-colors"

                      onClick={() => {
                        setDrugsDropdown(!drugsDropdown);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-gray-400" />
                        <span
                          className={
                            selectedDrug?.drug?.name
                              ? "text-gray-900"
                              : "text-gray-400"
                          }
                        >
                          {selectedDrug?.drug?.name ||
                            "Search and select a drug..."}
                        </span>
                      </div>
                    </div>

                    {/* Dropdown */}
                    {drugsDropdown && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                      >
                        <div className="p-3 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              ref={searchRef}
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              type="text"
                              onKeyDown={handleSearchKeyDown}
                              placeholder="Type to search..."
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div className="max-h-64 overflow-y-auto" ref={listContainerRef}>
                          {debouncedSearch?.length < 3 ? (
                            <p className="p-4 text-sm text-gray-500 text-center">
                              Please enter at least 3 characters
                            </p>
                          ) : loading ? (
                            <div className="p-4 flex items-center justify-center gap-2 text-gray-500">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span className="text-sm">Searching...</span>
                            </div>
                          ) : drugs?.length > 0 ? (
                            drugs?.map((drug_item, idx) => (
                              <div
                                key={idx}
                                onClick={() => selectDrug(drug_item)}
                                id={`drug-result-${idx}`}
                                className={`p-3 cursor-pointer transition-colors border-b border-gray-100 last:border-0 ${selectedResultIndex === idx
                                  ? "bg-blue-300"
                                  : "hover:bg-blue-50"
                                  }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-900">
                                    {drug_item?.drug?.name}
                                  </span>
                                  <span className="text-sm text-blue-600 font-semibold">
                                    GHC {drug_item?.drug?.cost_price}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500">
                                  Available: {drug_item?.quantity} units
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="p-4 text-sm text-gray-500 text-center">
                              No results found
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Batch, Quantity, Stock */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Drug Batch
                    </label>
                    <select
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value) {
                          setSelectedBatch(JSON.parse(value));
                        } else {
                          setSelectedBatch(null);
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select batch...</option>
                      {drugBatches?.map((batch, index) => (
                        <option
                          key={index}
                          value={JSON.stringify(batch)}
                          title={batch.expiry_date}
                        >
                          {batch.batch_number}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      onChange={(e) => setQuantityRequested(e.target.value)}
                      value={quantityRequested}
                      placeholder="0"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Available Stock
                    </label>
                    <div className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900">
                      {drugBatches?.length > 0
                        ? selectedBatch?.quantity || "—"
                        : selectedDrug?.quantity || "—"}
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Used/Damaged By <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="damaged_by"
                      onChange={(e) => updateSaleData(e)}
                      required
                      placeholder="Enter name..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unit Price
                    </label>
                    <div className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 flex items-center gap-2">
                      <span className="text-gray-400 font-semibold">₵</span>
                      {selectedDrug?.drug?.cost_price || "—"}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <div className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {drugBatches?.length > 0
                      ? selectedBatch?.expiry_date || "—"
                      : selectedDrug?.expiry_date || "—"}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => insertDrug()}
                  className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add to List
                </button>
                <button
                  type="submit"
                  disabled={recordingSale}
                  className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${recordingSale
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-500 hover:bg-green-600 text-white"
                    }`}
                >
                  {recordingSale ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Recording...
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4" />
                      Record Usage
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>

          {/* Items List Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-6">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Selected Items
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {insertedDrugs.length}{" "}
                  {insertedDrugs.length === 1 ? "item" : "items"} added
                </p>
              </div>

              <div className="max-h-[600px] overflow-y-auto">
                {insertedDrugs?.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {insertedDrugs?.map((drug, index) => (
                      <div
                        key={index}
                        className="p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 text-sm mb-1">
                              {drug?.drug_name}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Hash className="w-3 h-3" />
                                ID: {drug?.drug}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => removeInsertedDrug(index)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">Qty</span>
                            <p className="font-semibold text-gray-900">
                              {drug?.quantity}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Unit</span>
                            <p className="font-semibold text-gray-900">
                              GHC {drug?.unit_price}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Total</span>
                            <p className="font-semibold text-blue-600">
                              GHC {drug?.total_cost}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No items added yet</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add drugs from the form
                    </p>
                  </div>
                )}
              </div>

              {/* Total */}
              {insertedDrugs?.length > 0 && (
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Total Cost
                    </span>
                    <span className="text-2xl font-bold text-gray-900">
                      GHC {totalCost}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Damages;
